import { TokenManager, authenticatedFetch } from "@/supabase/supabaseClient";
import { FileUploadService } from "./fileUploadService";
import type {
  Task,
  Step,
  CreateTaskForm,
  CreateStepForm,
  UpdateStepPayload,
  LookupValue,
  TemplateTask,
  TemplateStep,
  TaskAssigneeType,
} from "@/types/task";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

export class TaskService {
  // Get tasks assigned to current user
  static async getUserTasks(): Promise<Task[]> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot fetch user tasks");
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/tasks?select=*,category:lookup_values!category_id(*),status:lookup_values!status_id(*),priority:lookup_values!priority_id(*),steps(*)&order=created_at.desc`
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error fetching user tasks:", error);
        throw new Error(`Failed to fetch user tasks: ${response.statusText}`);
      }

      const data = await response.json();
      return data || [];
    } catch (error) {
      console.error("Error in getUserTasks:", error);
      throw error;
    }
  }

  // Get single task with steps
  static async getTask(taskId: string): Promise<Task | null> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot fetch task");
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/tasks?id=eq.${taskId}&select=*,category:lookup_values!category_id(*),status:lookup_values!status_id(*),priority:lookup_values!priority_id(*),steps(*)`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch task: ${response.statusText}`);
      }

      const data = await response.json();
      return data?.[0] || null;
    } catch (error) {
      console.error("Error in getTask:", error);
      throw error;
    }
  }

  // Get tasks by asset ID
  static async getTasksByAssetId(assetId: string): Promise<Task[]> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot fetch tasks by asset");
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/tasks?asset_id=eq.${assetId}&select=*,category:lookup_values!category_id(*),status:lookup_values!status_id(*),priority:lookup_values!priority_id(*),steps(*)&order=created_at.desc`
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error fetching tasks by asset:", error);
        throw new Error(
          `Failed to fetch tasks by asset: ${response.statusText}`
        );
      }

      const data = await response.json();
      return data || [];
    } catch (error) {
      console.error("Error in getTasksByAssetId:", error);
      throw error;
    }
  }

  // Create new task
  static async createTask(taskData: CreateTaskForm): Promise<Task> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot create task");
        throw new Error("Authentication token not available");
      }

      // Get current user from stored token data
      const user = await TokenManager.getStoredUserAsync();
      if (!user) {
        throw new Error("You must be logged in to create a task.");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/tasks`,
        {
          method: "POST",
          headers: {
            Prefer: "return=representation",
          },
          body: JSON.stringify({
            ...taskData,
            created_by: user.id,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error creating task:", error);
        throw new Error(`Failed to create task: ${response.statusText}`);
      }

      const data = await response.json();
      return data && data.length > 0 ? data[0] : data;
    } catch (error) {
      console.error("Error in createTask:", error);
      throw error;
    }
  }

  // Update task
  static async updateTask(
    taskId: string,
    updates: Partial<Task>
  ): Promise<Task> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot update task");
        throw new Error("Authentication token not available");
      }

      // First get the current task to check its status
      const currentTaskResponse = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/tasks?select=status_id,completed_at&id=eq.${taskId}`
      );

      if (!currentTaskResponse.ok) {
        throw new Error("Failed to get current task status");
      }

      const currentTaskData = await currentTaskResponse.json();
      const currentTask = currentTaskData?.[0];

      const updateData: Partial<Task> & {
        completed_at?: string | null;
      } = { ...updates };

      // If status_id is being updated, check if it's changing to/from completed
      if (updates.status_id) {
        // Get all statuses to find the completed status ID
        const statusResponse = await authenticatedFetch(
          `${supabaseUrl}/rest/v1/lookup_values?select=id,value,label&category=eq.task_status`
        );

        let completedStatusId = null;
        if (statusResponse.ok) {
          const statuses = await statusResponse.json();
          const completedStatus = statuses.find(
            (s: LookupValue) =>
              s.value === "completed" ||
              s.label.toLowerCase().includes("completed")
          );
          completedStatusId = completedStatus?.id;
        }

        // If changing to completed status and wasn't completed before
        if (
          completedStatusId &&
          updates.status_id === completedStatusId &&
          !currentTask?.completed_at
        ) {
          console.log(
            "🎉 Task will be automatically completed by database trigger"
          );
        }
        // If changing from completed to another status
        else if (
          completedStatusId &&
          updates.status_id !== completedStatusId &&
          currentTask?.completed_at
        ) {
          console.log(
            "🔄 Task completion will be automatically cleared by database trigger"
          );
        }
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/tasks?id=eq.${taskId}`,
        {
          method: "PATCH",
          headers: {
            Prefer: "return=representation",
          },
          body: JSON.stringify(updateData),
        }
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error updating task:", error);
        throw new Error(`Failed to update task: ${response.statusText}`);
      }

      const data = await response.json();
      if (!data || data.length === 0) {
        throw new Error(
          "Task not found or you do not have permission to update it."
        );
      }

      return data[0];
    } catch (error) {
      console.error("Error in updateTask:", error);
      throw error;
    }
  }

  // Delete task
  static async deleteTask(taskId: string): Promise<void> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot delete task");
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/tasks?id=eq.${taskId}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error deleting task:", error);
        throw new Error(`Failed to delete task: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error in deleteTask:", error);
      throw error;
    }
  }

  // Create step for task
  static async createStep(
    taskId: string,
    stepData: CreateStepForm
  ): Promise<Step> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot create step");
        throw new Error("Authentication token not available");
      }

      // Create a cleaned version of the step data
      const cleanedStepData: Record<string, unknown> = {
        task_id: taskId,
      };

      // Copy all fields from stepData, handling empty strings appropriately
      Object.entries(stepData).forEach(([key, value]) => {
        if (value === undefined) {
          // Skip undefined values
          return;
        }

        // Convert empty strings to null for timestamp fields
        if (
          value === "" &&
          (key === "completed_at" ||
            key.endsWith("_at") ||
            key.endsWith("_date"))
        ) {
          cleanedStepData[key] = null;
        }
        // Skip empty strings for non-timestamp fields (don't send them)
        else if (
          value === "" &&
          !(
            key === "completed_at" ||
            key.endsWith("_at") ||
            key.endsWith("_date")
          )
        ) {
          // Skip empty strings for non-timestamp fields
          return;
        }
        // Include all other values
        else {
          cleanedStepData[key] = value;
        }
      });

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/steps`,
        {
          method: "POST",
          headers: {
            Prefer: "return=representation",
          },
          body: JSON.stringify(cleanedStepData),
        }
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error creating step:", error);
        throw new Error(`Failed to create step: ${response.statusText}`);
      }

      const data = await response.json();
      return data && data.length > 0 ? data[0] : data;
    } catch (error) {
      console.error("Error in createStep:", error);
      throw error;
    }
  }

  // Update step
  static async updateStep(
    stepId: string,
    updates: UpdateStepPayload
  ): Promise<Step> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot update step");
        throw new Error("Authentication token not available");
      }

      // Create a cleaned version of the update data
      const updateData: Record<string, unknown> = {};

      // Copy all fields from updates, handling empty strings appropriately
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined) {
          // Skip undefined values
          return;
        }

        // Convert empty strings to null for timestamp fields
        if (
          value === "" &&
          (key === "completed_at" ||
            key.endsWith("_at") ||
            key.endsWith("_date"))
        ) {
          updateData[key] = null;
        }
        // Skip empty strings for non-timestamp fields (don't send them)
        else if (
          value === "" &&
          !(
            key === "completed_at" ||
            key.endsWith("_at") ||
            key.endsWith("_date")
          )
        ) {
          // Skip empty strings for non-timestamp fields
          return;
        }
        // Include all other values
        else {
          updateData[key] = value;
        }
      });

      // If marking as completed, let database trigger handle timestamps automatically
      if (updates.is_completed === true) {
        console.log(
          "🎉 Step completion will be automatically handled by database trigger"
        );
      } else if (updates.is_completed === false) {
        console.log(
          "🔄 Step completion will be automatically cleared by database trigger"
        );
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/steps?id=eq.${stepId}`,
        {
          method: "PATCH",
          headers: {
            Prefer: "return=representation",
          },
          body: JSON.stringify(updateData),
        }
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error updating step:", error);
        throw new Error(`Failed to update step: ${response.statusText}`);
      }

      const data = await response.json();
      if (!data || data.length === 0) {
        throw new Error(
          "Step not found or you do not have permission to update it."
        );
      }

      return data[0];
    } catch (error) {
      console.error("Error in updateStep:", error);
      throw error;
    }
  }

  // Delete step
  static async deleteStep(stepId: string): Promise<void> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot delete step");
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/steps?id=eq.${stepId}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error deleting step:", error);
        throw new Error(`Failed to delete step: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error in deleteStep:", error);
      throw error;
    }
  }

  // Get lookup values by category
  static async getLookupValues(category: string): Promise<LookupValue[]> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot fetch lookup values");
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/lookup_values?select=*&category=eq.${encodeURIComponent(
          category
        )}&is_active=eq.true&order=sort_order`
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error fetching lookup values:", error);
        throw new Error(
          `Failed to fetch lookup values: ${response.statusText}`
        );
      }

      const data = await response.json();
      return data || [];
    } catch (error) {
      console.error("Error in getLookupValues:", error);
      throw error;
    }
  }

  // Get all active templates
  static async getTemplates(): Promise<TemplateTask[]> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot fetch templates");
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/template_tasks?select=*,category:lookup_values!category_id(*),priority:lookup_values!priority_id(*),status:lookup_values!status_id(*),template_steps(*)&is_active=eq.true&order=name`
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error fetching templates:", error);
        throw new Error(`Failed to fetch templates: ${response.statusText}`);
      }

      const data = await response.json();
      return data || [];
    } catch (error) {
      console.error("Error in getTemplates:", error);
      throw error;
    }
  }

  // Get templates by layer name
  static async getTemplatesByLayer(layerName: string): Promise<TemplateTask[]> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot fetch templates by layer");
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/template_tasks?select=*,category:lookup_values!category_id(*),priority:lookup_values!priority_id(*),status:lookup_values!status_id(*),template_steps(*)&is_active=eq.true&layer_name=eq.${encodeURIComponent(
          layerName
        )}&order=name`
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error fetching templates by layer:", error);
        throw new Error(
          `Failed to fetch templates by layer: ${response.statusText}`
        );
      }

      const data = await response.json();
      return data || [];
    } catch (error) {
      console.error("Error in getTemplatesByLayer:", error);
      throw error;
    }
  }

  // Create task from template
  static async createTaskFromTemplate(
    templateId: string,
    taskData: CreateTaskForm
  ): Promise<Task> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot create task from template");
        throw new Error("Authentication token not available");
      }

      // Get template with steps
      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/template_tasks?select=*,template_steps(*)&id=eq.${templateId}`
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error fetching template:", error);
        throw new Error(`Failed to fetch template: ${response.statusText}`);
      }

      const templateData = await response.json();
      if (!templateData || templateData.length === 0) {
        throw new Error("Template not found");
      }

      const template = templateData[0];

      // Create task with template defaults
      const newTask = await this.createTask({
        ...taskData,
        category_id: taskData.category_id || template.category_id,
        priority_id: taskData.priority_id || template.priority_id,
      });

      // Create steps from template
      if (template.template_steps && template.template_steps.length > 0) {
        const stepPromises = template.template_steps.map(
          (templateStep: TemplateStep) =>
            this.createStep(newTask.id, {
              step_name: templateStep.step_name, // Use step_name directly
              description: templateStep.description,
              step_order: templateStep.step_order,
              assignee_type: templateStep.default_assignee_type,
              assignee: templateStep.default_assignee,
              sequence: templateStep.sequence,
            })
        );

        await Promise.all(stepPromises);
      }

      return newTask;
    } catch (error) {
      console.error("Error in createTaskFromTemplate:", error);
      throw error;
    }
  }

  // Upload file to Supabase Storage
  static async uploadFile(file: File): Promise<string> {
    const result = await FileUploadService.uploadFile(file, "general");
    return result.url;
  }

  // Upload task attachment
  static async uploadTaskAttachment(
    taskId: string,
    file: File
  ): Promise<string> {
    const result = await FileUploadService.uploadTaskAttachment(taskId, file);
    return result.url;
  }

  // Upload task photo
  static async uploadTaskPhoto(taskId: string, file: File): Promise<string> {
    const result = await FileUploadService.uploadTaskPhoto(taskId, file);
    return result.url;
  }

  // Template CRUD operations

  /**
   * Create a new template with steps
   */
  static async createTemplate(
    templateData: {
      name: string;
      description?: string;
      layer_name: string;
      category_id?: string;
      priority_id?: string;
      status_id?: string;
      default_assignee_type?: TaskAssigneeType;
      default_assignee?: string;
      default_due_days?: number;
      default_start_days?: number;
      auto_assign_steps?: boolean;
      require_photos?: boolean;
      require_geometry?: boolean;
      allowed_geometry_types?: ("Point" | "LineString" | "Polygon")[];
    },
    templateSteps: TemplateStep[] = []
  ): Promise<TemplateTask> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot create template");
        throw new Error("Authentication token not available");
      }

      // Get current user from stored token data
      const user = await TokenManager.getStoredUserAsync();
      if (!user) {
        throw new Error("You must be logged in to create a template.");
      }

      // Create the template
      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/template_tasks`,
        {
          method: "POST",
          headers: {
            Prefer: "return=representation",
          },
          body: JSON.stringify({
            name: templateData.name.trim(),
            description: templateData.description?.trim() || null,
            layer_name: templateData.layer_name,
            category_id: templateData.category_id || null,
            priority_id: templateData.priority_id || null,
            status_id: templateData.status_id || null,
            default_assignee_type: templateData.default_assignee_type || null,
            default_assignee: templateData.default_assignee || null,
            default_due_days: templateData.default_due_days || null,
            default_start_days: templateData.default_start_days || null,
            auto_assign_steps: templateData.auto_assign_steps || false,
            require_photos: templateData.require_photos || false,
            require_geometry: templateData.require_geometry || false,
            allowed_geometry_types: templateData.allowed_geometry_types || null,
            is_active: true,
            created_by: user.id,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error creating template:", errorText);
        throw new Error(`Failed to create template: ${response.statusText}`);
      }

      const templateResult = await response.json();
      const template =
        templateResult && templateResult.length > 0
          ? templateResult[0]
          : templateResult;

      // Create template steps if any
      if (templateSteps.length > 0) {
        const stepsToCreate = templateSteps.map((step, index) => ({
          template_task_id: template.id,
          step_name: step.step_name,
          description: step.description || null,
          step_order: index,
          sequence: step.sequence,
          default_assignee_type: step.default_assignee_type || null,
          default_assignee: step.default_assignee || null,
          is_required: step.is_required || false,
          auto_complete: step.auto_complete || false,
          requires_photos: step.requires_photos || false,
          requires_notes: step.requires_notes || false,
          reference_link: step.reference_link || null,
          survey_link: step.survey_link || null,
          depends_on_step_ids: step.depends_on_step_ids || null,
          completion_criteria: step.completion_criteria || null,
          reminder_days_before: step.reminder_days_before || null,
          notification_on_completion: step.notification_on_completion || false,
          escalation_days: step.escalation_days || null,
        }));

        const stepsResponse = await authenticatedFetch(
          `${supabaseUrl}/rest/v1/template_steps`,
          {
            method: "POST",
            headers: {
              Prefer: "return=representation",
            },
            body: JSON.stringify(stepsToCreate),
          }
        );

        if (!stepsResponse.ok) {
          const stepsError = await stepsResponse.text();
          console.error("Error creating template steps:", stepsError);
          // Don't throw here, just log the error as the template was created successfully
        }
      }

      return template;
    } catch (error) {
      console.error("Error in createTemplate:", error);
      throw error;
    }
  }

  /**
   * Update an existing template with steps
   */
  static async updateTemplate(
    templateId: string,
    templateData: {
      name: string;
      description?: string;
      layer_name: string;
      category_id?: string;
      priority_id?: string;
      status_id?: string;
      default_assignee_type?: TaskAssigneeType;
      default_assignee?: string;
      default_due_days?: number;
      default_start_days?: number;
      is_active?: boolean;
      auto_assign_steps?: boolean;
      require_photos?: boolean;
      require_geometry?: boolean;
      allowed_geometry_types?: ("Point" | "LineString" | "Polygon")[];
    },
    templateSteps: TemplateStep[] = []
  ): Promise<TemplateTask> {
    try {
      console.log(
        "🔄 Updating template:",
        templateId,
        "with data:",
        templateData
      );

      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot update template");
        throw new Error("Authentication token not available");
      }

      // Update the template
      const updateResponse = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/template_tasks?id=eq.${templateId}`,
        {
          method: "PATCH",
          headers: {
            Prefer: "return=representation",
          },
          body: JSON.stringify({
            name: templateData.name.trim(),
            description: templateData.description?.trim() || null,
            layer_name: templateData.layer_name,
            category_id: templateData.category_id || null,
            priority_id: templateData.priority_id || null,
            status_id: templateData.status_id || null,
            default_assignee_type: templateData.default_assignee_type || null,
            default_assignee: templateData.default_assignee || null,
            default_due_days: templateData.default_due_days || null,
            default_start_days: templateData.default_start_days || null,
            is_active:
              templateData.is_active !== undefined
                ? templateData.is_active
                : true,
            auto_assign_steps: templateData.auto_assign_steps || false,
            require_photos: templateData.require_photos || false,
            require_geometry: templateData.require_geometry || false,
            allowed_geometry_types: templateData.allowed_geometry_types || null,
            // Remove updated_at to let database handle it with its own timestamp
          }),
        }
      );

      if (!updateResponse.ok) {
        const templateError = await updateResponse.text();
        console.error("❌ Error updating template:", templateError);
        throw new Error(
          `Failed to update template: ${updateResponse.statusText}`
        );
      }

      console.log("✅ Template updated successfully");

      // Delete existing template steps
      const deleteStepsResponse = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/template_steps?template_task_id=eq.${templateId}`,
        {
          method: "DELETE",
        }
      );

      if (!deleteStepsResponse.ok) {
        const deleteStepsError = await deleteStepsResponse.text();
        console.error("Error deleting old template steps:", deleteStepsError);
      }

      // Create new template steps if any
      if (templateSteps.length > 0) {
        const stepsToCreate = templateSteps.map((step, index) => ({
          template_task_id: templateId,
          step_name: step.step_name,
          description: step.description || null,
          step_order: index,
          sequence: step.sequence,
          default_assignee_type: step.default_assignee_type || null,
          default_assignee: step.default_assignee || null,
          is_required: step.is_required || false,
          auto_complete: step.auto_complete || false,
          requires_photos: step.requires_photos || false,
          requires_notes: step.requires_notes || false,
          reference_link: step.reference_link || null,
          survey_link: step.survey_link || null,
          depends_on_step_ids: step.depends_on_step_ids || null,
          completion_criteria: step.completion_criteria || null,
          reminder_days_before: step.reminder_days_before || null,
          notification_on_completion: step.notification_on_completion || false,
          escalation_days: step.escalation_days || null,
        }));

        const stepsResponse = await authenticatedFetch(
          `${supabaseUrl}/rest/v1/template_steps`,
          {
            method: "POST",
            headers: {
              Prefer: "return=representation",
            },
            body: JSON.stringify(stepsToCreate),
          }
        );

        if (!stepsResponse.ok) {
          const stepsError = await stepsResponse.text();
          console.error("Error creating new template steps:", stepsError);
        }
      }

      // Fetch the complete template with steps to return
      const completeTemplateResponse = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/template_tasks?select=*,category:lookup_values!category_id(*),priority:lookup_values!priority_id(*),template_steps(*)&id=eq.${templateId}`
      );

      if (!completeTemplateResponse.ok) {
        const fetchError = await completeTemplateResponse.text();
        console.error("Error fetching updated template:", fetchError);
        throw new Error(
          `Failed to fetch updated template: ${completeTemplateResponse.statusText}`
        );
      }

      const completeTemplateData = await completeTemplateResponse.json();
      if (!completeTemplateData || completeTemplateData.length === 0) {
        throw new Error("Updated template not found");
      }

      return completeTemplateData[0];
    } catch (error) {
      console.error("Error in updateTemplate:", error);
      throw error;
    }
  }

  /**
   * Delete a template (and its steps)
   */
  static async deleteTemplate(templateId: string): Promise<void> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot delete template");
        throw new Error("Authentication token not available");
      }

      // Delete template steps first (due to foreign key constraint)
      const stepsResponse = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/template_steps?template_task_id=eq.${templateId}`,
        {
          method: "DELETE",
        }
      );

      if (!stepsResponse.ok) {
        const stepsError = await stepsResponse.text();
        console.error("Error deleting template steps:", stepsError);
      }

      // Delete the template
      const templateResponse = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/template_tasks?id=eq.${templateId}`,
        {
          method: "DELETE",
        }
      );

      if (!templateResponse.ok) {
        const templateError = await templateResponse.text();
        console.error("Error deleting template:", templateError);
        throw new Error(
          `Failed to delete template: ${templateResponse.statusText}`
        );
      }
    } catch (error) {
      console.error("Error in deleteTemplate:", error);
      throw error;
    }
  }

  // Get tasks by location with buffer
  static async getTasksByLocation(
    longitude: number,
    latitude: number,
    bufferMeters: number = 100
  ): Promise<Task[]> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot fetch tasks by location");
        throw new Error("Authentication token not available");
      }

      // Build the query to fetch all tasks with geometry
      const query = `geom=not.is.null&order=created_at.desc`;

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/tasks?select=*,category:lookup_values!category_id(*),status:lookup_values!status_id(*),priority:lookup_values!priority_id(*),steps(*)&${query}`,
        {
          headers: {
            // Use PostGIS function in a custom header filter
            "Range-Unit": "items",
            Prefer: `params=single-object`,
          },
        }
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error fetching tasks by location:", error);
        throw new Error(
          `Failed to fetch tasks by location: ${response.statusText}`
        );
      }

      const allTasks = await response.json();

      // Filter tasks client-side using Haversine distance calculation
      const filteredTasks = allTasks.filter((task: Task) => {
        if (!task.geom) return false;

        try {
          const geometry =
            typeof task.geom === "string" ? JSON.parse(task.geom) : task.geom;

          if (geometry.type === "Point") {
            const [taskLng, taskLat] = geometry.coordinates;
            const distance = this.calculateDistance(
              latitude,
              longitude,
              taskLat,
              taskLng
            );
            return distance <= bufferMeters;
          } else if (geometry.type === "LineString") {
            // For LineString, check if any point is within buffer
            return geometry.coordinates.some(
              ([taskLng, taskLat]: [number, number]) => {
                const distance = this.calculateDistance(
                  latitude,
                  longitude,
                  taskLat,
                  taskLng
                );
                return distance <= bufferMeters;
              }
            );
          } else if (geometry.type === "Polygon") {
            // For Polygon, check if any vertex is within buffer (simplified approach)
            return geometry.coordinates[0].some(
              ([taskLng, taskLat]: [number, number]) => {
                const distance = this.calculateDistance(
                  latitude,
                  longitude,
                  taskLat,
                  taskLng
                );
                return distance <= bufferMeters;
              }
            );
          }

          return false;
        } catch (error) {
          console.error("Error parsing task geometry:", error);
          return false;
        }
      });

      return filteredTasks || [];
    } catch (error) {
      console.error("Error in getTasksByLocation:", error);
      throw error;
    }
  }

  // Helper method to calculate distance between two points (Haversine formula)
  private static calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }
}
